import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { Box, Button, Grid, Paper, Stack, TableContainer, Typography } from '@mui/material'

import { useCheckIfExists } from '@/hooks/useCheckIfExists'

import PageContainer from '@/components/ui/containers/PageContainer'
import TextInput from '@/components/ui/inputs/TextInput'
import Pagination from '@/components/ui/pagination/Pagination'

import StaffsTable, { StaffData } from '@/pages/staffs/components/StaffsTable'

import { Staff } from '@/core/models/staff.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getConsultation, selectConsultationById, selectConsultationUpdateRequestTracker, sendInvite, updateConsultation } from '@/redux/reducers/consultation.reducer'
import {
  addStaff,
  getStaffs,
  selectNewlyCreatedStaffId,
  selectStaffCreateRequestTracker,
  selectStaffListRequestTracker,
  selectStaffs,
  selectStaffSearchParams,
  selectStaffSearchResult,
} from '@/redux/reducers/staff.reducer'

import { sanitizeStaffCreation } from '@/utils/staff'
import PluginCardContainer from './containers/PluginCard'
import { ConsultationParticipantType, ConsultationStatus } from '@/core/models/consultation.model'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useSnackbar } from 'notistack'
import { isStaff } from '@/utils/type-guards'

const rowsPerPageOptions = [5, 10, 15]

function NetsoinsPage() {
  const { consultationId } = useParams()

  const [selectedRow, setSelectedRow] = useState<StaffData>()
  const { t } = useTranslation()
  const [itemsPerPage, setItemsPerPage] = useState<number>(rowsPerPageOptions[0])
  const [page, setPage] = useState<number>(1)

  const staffs = useAppSelector(selectStaffs)
  const authUser = useAppSelector(selectAuthUser)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const staffSearchParams = useAppSelector(selectStaffSearchParams)
  const staffSearchResult = useAppSelector(selectStaffSearchResult)
  const staffListRequestTracker = useAppSelector(selectStaffListRequestTracker)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const defaultTextSearch = ''

  useEffect(() => {
    if (consultationId) {
      dispatch(getConsultation(consultationId))
    }
  }, [dispatch, consultationId])

  useEffect(() => {
    const query = {
      name: '',
      surname: '',
    }
    const options = {
      limit: 5,
      skip: 0,
      orderBy: '',
    }
    dispatch(getStaffs(query, options))
  }, [dispatch])

  const staffsData = useMemo(() => {
    const getContactStatus = (staffId: string): StaffData['contactStatus'] => {
      const contact = _.find(isStaff(authUser) ? authUser?.contacts : [], ({ contact }) => contact === staffId)

      if (!contact) return 'notRequested'

      switch (contact.status) {
        case 'pending':
          return contact.requestBy === authUser?._id ? 'pending' : 'handle'
        case 'accepted':
        case 'refused':
          return contact.status
        default:
          return 'notRequested'
      }
    }
    return _.map(staffs, (staff) => ({
      id: staff._id,
      name: staff.userInfo.name,
      surname: staff.userInfo.surname,
      gender: staff.userInfo.gender || 'I',
      city: staff.contactInfo?.city,
      cps: staff.cps,
      email: staff.email,
      pluginId: staff.pluginId ? staff.pluginId : '',
      contactStatus: getContactStatus(staff._id),
    }))
  }, [staffs, authUser])

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const textSearch = event.target.value
    const query = {
      name: textSearch,
      surname: textSearch,
      cps: textSearch,
    }
    dispatch(
      getStaffs(query, {
        limit: _.get(staffSearchParams.query, 'options.limit', rowsPerPageOptions[0]),
      })
    )
  }, 300)

  const updateConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.consultation.update.success'))
      navigate(`/consultations/${consultationId}/live`)
    },
    onError: () => {
      enqueueSnackbar(t('notification.consultation.update.error'))
    },
    requestTrackerSelector: selectConsultationUpdateRequestTracker
  })

  // the medecin might come from netsoins
  // so its necessary to check if its exists or not
  const checkStaff = useCheckIfExists<Staff>({
    successMsg: t('notification.medecin.create.success'),
    errorMsg: t('notification.medecin.create.error'),
    itemTracker: selectStaffCreateRequestTracker,
    createAction: (staff: Staff) => addStaff(sanitizeStaffCreation(_.omit(staff, ['_id', 'id', 'contactStatus']))),
    latestIDSelector: useAppSelector(selectNewlyCreatedStaffId),
    itemList: useAppSelector(selectStaffs),
    callback: (staff: Staff) => {
      if (!staff) return
      if (authUser && consultationId) {
        // update consultation object
        // and send invite to medecin...
        // need to add a request handler so we are sure that the consultation has all the field needed
        updateConsultationRequestHandler.dispatch(
          updateConsultation(consultationId, {
            medecin: staff._id,
            status: ConsultationStatus.Active,
          })
        )
        dispatch(
          sendInvite(consultationId, {
            email: staff.email,
            // name: staff.userInfo.name,
            // surname: staff.userInfo.surname,
            type: ConsultationParticipantType.DOCTOR,
          })
        )
      }
    },
  })

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    dispatch(
      getStaffs(staffSearchParams.query, {
        limit: itemsPerPage,
        skip: (newPage - 1) * itemsPerPage,
      })
    )
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage === newItemsPerPage) return
    setPage(1)
    setItemsPerPage(newItemsPerPage)
    dispatch(getStaffs(staffSearchParams.query, { limit: newItemsPerPage }))
  }

  const onMedecinClick = (staff: StaffData) => {
    setSelectedRow(staff)
    // if (staff.id && id) {
    //     dispatch(updateConsultation(id, {
    //             medecin: staff.id
    //         }
    //     ))
    // }
  }

  const onContinueWithoutMedecin = () => {
    navigate(`/consultations/${consultationId}/local`)
  }

  const onContinueWithMedecin = () => {
    if (selectedRow?.id !== undefined) {
      checkStaff(selectedRow.id)
    }
  }

  if (!consultation) return null

  return (
    <PageContainer>
      <Grid container spacing={2}>

        <Grid container item xs={12} spacing={4}>
          <Grid item xs={6} >
            <Stack spacing={2}>
              <Typography variant="h2">{t('page.netsoins.title')}</Typography>
              <PluginCardContainer />

              <Typography variant="body1" display="block">
                {t('page.netsoins.description')}
                {/* TODO : i need line breaks here :(  */}
              </Typography>


              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button disableElevation variant="contained" onClick={() => onContinueWithoutMedecin()}>
                  {t('page.netsoins.continueWithoutDoctorButton.label')}
                </Button>
                <Button disableElevation variant="contained" disabled={selectedRow === undefined} onClick={() => onContinueWithMedecin()}>
                  {t('page.netsoins.continueWithSelectedDoctorButton.label')}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={8} xl={9}>
            <TextInput defaultValue={defaultTextSearch} fullWidth onChange={debouncedOnChange} placeholder={t('page.staffs.searchInput.placeholder')} />
          </Grid>
        </Grid>

        <Grid container item xs={12} spacing={4}>
          <Grid item xs={12} lg={12} xl={9}>
            {
              <TableContainer component={Paper}>
                <StaffsTable data={staffsData} autoHightlight={false} showAction={false} loading={staffListRequestTracker.loading} onSelect={onMedecinClick} />
                <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                  <Pagination
                    color="primary"
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    onItemsPerPageChange={handleChangeItemsPerPage}
                    totalItems={_.get(staffSearchResult, 'totalCount', 0)}
                  />
                </Box>
              </TableContainer>
            }
          </Grid>
        </Grid>
      </Grid>
    </PageContainer>
  )
}

export default NetsoinsPage
